import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatGBP, customerApi } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { useCart } from "@/context/CartContext";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { ProductCard } from "@/components/ProductCard";
import { ProductBadges } from "@/components/ProductBadges";
import { ProductInfoSections } from "@/components/ProductInfoSections";
import { LiveActivity } from "@/components/LiveActivity";
import { CustomiseFitModal, CustomFitSummary } from "@/components/CustomiseFit";
import { isAlterable, availableMeasurements } from "@/lib/alterations";
import { Seo } from "@/components/Seo";
import { productSchema, breadcrumbSchema } from "@/lib/seo";
import { colourHex, isLightColour } from "@/lib/productOptions";
import { Heart, ShoppingBag, Plus, Minus, Truck, ShieldCheck, ChevronRight, ChevronLeft, Ruler, Scissors, Check, Share2, Tag, X } from "lucide-react";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const { addToCart, wishlist, toggleWishlist } = useCart();
  const { customer } = useCustomerAuth();
  const { format } = useCurrency();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [loadedSrc, setLoadedSrc] = useState(null);
  const touchStartX = useRef(null);
  const touchDeltaX = useRef(0);
  const swipedRef = useRef(false);
  const [lightbox, setLightbox] = useState(false);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [customization, setCustomization] = useState(null);
  const [customConfirmed, setCustomConfirmed] = useState(false);
  const [fitOpen, setFitOpen] = useState(false);
  const fetchedSlug = useRef(null);

  const copyToClipboard = async (text) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch { /* fall through to legacy */ }
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.top = "-9999px";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    const shareData = {
      title: product ? `${product.name} - Zebaa` : "Zebaa",
      text: product ? `Check out ${product.name} at Zebaa` : "",
      url,
    };
    // Prefer native share when actually available & allowed.
    if (navigator.share && (!navigator.canShare || navigator.canShare(shareData))) {
      try {
        await navigator.share(shareData);
        return;
      } catch (e) {
        if (e && e.name === "AbortError") return; // user dismissed the sheet
        // otherwise fall back to copying the link
      }
    }
    const copied = await copyToClipboard(url);
    if (copied) toast.success("Link copied to clipboard");
    else toast.error("Could not copy link");
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    setQty(1);
    // Guard against double-fetch (StrictMode dev double-invoke + auth resolving),
    // which would otherwise increment the view counter twice per visit.
    if (fetchedSlug.current === slug) return;
    fetchedSlug.current = slug;
    api.get(`/products/${slug}`).then(({ data }) => {
      setProduct(data);
      setSelectedSize(null);
      const cs = data.colour_stock && data.colour_stock.length ? data.colour_stock : null;
      const firstColour = cs
        ? (cs.find((c) => (c.stock || 0) > 0) || cs[0]).colour
        : (data.colors && data.colors.length ? data.colors[0] : null);
      setSelectedColor(firstColour);
      api.get("/products", { params: { category: data.category } }).then((r) => {
        setRelated(r.data.filter((p) => p.slug !== slug).slice(0, 4));
      });
    });
  }, [slug]);

  // Record recently-viewed once the customer is known (does not re-fetch the product).
  useEffect(() => {
    setActiveImg(0);
    setQty(1);
  }, [selectedColor, selectedSize]);

  useEffect(() => {
    if (customer && product?.id) {
      customerApi().post("/account/recently-viewed", { product_id: product.id }).catch(() => {});
    }
  }, [customer, product?.id]);

  if (!product) {
    return (
      <div className="max-w-[1800px] mx-auto px-5 sm:px-8 lg:px-16 py-10 min-h-[80vh]" data-testid="product-detail-loading">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 animate-pulse">
          <div className="aspect-[3/4] bg-surface" />
          <div className="space-y-4 lg:py-6">
            <div className="h-3 w-24 bg-surface" />
            <div className="h-10 w-2/3 bg-surface" />
            <div className="h-4 w-40 bg-surface" />
            <div className="h-6 w-28 bg-surface mt-6" />
            <div className="h-px w-full bg-gray-100 my-6" />
            <div className="h-4 w-full bg-surface" />
            <div className="h-4 w-5/6 bg-surface" />
            <div className="h-12 w-full bg-surface mt-8" />
          </div>
        </div>
      </div>
    );
  }

  const wished = wishlist.includes(product.id);
  // Only offer the sizes this piece actually comes in (bangle/ring sizes); most jewellery is one size.
  const availableSizes = product.sizes || [];
  // Per-colour stock: prefer colour_stock; fall back to legacy colors[].
  const colourStock = (product.colour_stock && product.colour_stock.length)
    ? product.colour_stock
    : (product.colors || []).map((c) => ({ colour: c, stock: null }));
  const stockFor = (colour) => {
    const cs = colourStock.find((c) => c.colour === colour);
    if (cs) return cs.stock;
    // Single-option piece: use the live total (null = stock not tracked).
    return colourStock.length ? null : (product.stock_left ?? null);
  };
  // Per-variant (colour × size) stock when available.
  const variantStock = product.variant_stock || [];
  const hasVariant = variantStock.length > 0;
  const variantStockFor = (colour, size) => {
    const v = variantStock.find((x) => (x.colour ?? null) === (colour ?? null) && (x.size ?? null) === (size ?? null));
    return v ? v.stock : 0;
  };
  const sizeSoldOut = (size) => hasVariant && (product.sizes || []).length > 0 && variantStockFor(selectedColor, size) <= 0;
  const sizesExist = (product.sizes || []).length > 0;
  // Total stock for a colour across all its sizes (null = not tracked).
  const colourStockTotal = (colour) => {
    if (!hasVariant) return stockFor(colour);
    if (sizesExist) {
      const rows = variantStock.filter((x) => (x.colour ?? null) === (colour ?? null));
      if (!rows.length) return null;
      return rows.reduce((s, v) => s + (v.stock || 0), 0);
    }
    return variantStockFor(colour, null);
  };
  const exactStock = hasVariant
    ? variantStockFor(selectedColor, sizesExist ? selectedSize : null)
    : stockFor(selectedColor);
  // Before a size is chosen we don't know the exact variant stock, so treat it as
  // unknown (null) rather than 0 — otherwise the page wrongly shows "Out of stock".
  const selectedStock = sizesExist && !selectedSize ? null : exactStock;
  const soldOut = sizesExist && !selectedSize
    ? (colourStockTotal(selectedColor) !== null && colourStockTotal(selectedColor) <= 0)
    : hasVariant
      ? (exactStock !== null && exactStock <= 0)
      : (exactStock !== null && exactStock <= 0);
  const needsSize = availableSizes.length > 0 && !selectedSize;
  // Never let the picker exceed the stock allocated to the chosen colour+size.
  const maxQty = (typeof selectedStock === "number" && selectedStock > 0) ? selectedStock : null;
  const alterable = isAlterable(product);
  const fitRows = alterable && selectedSize ? availableMeasurements(product, selectedSize) : [];
  const canCustomise = alterable && !!selectedSize && fitRows.length > 0;
  const prefix = `${product.name} - ${product.subtitle}.`;
  const cleanDescription =
    product.description && product.description.startsWith(prefix)
      ? product.description.slice(prefix.length).trim()
      : product.description || "";

  const colourImages = (product.colour_images || []).find(
    (c) => c.colour === selectedColor && (c.images || []).filter(Boolean).length
  );
  const images = (colourImages
    ? colourImages.images
    : (Array.isArray(product.images) && product.images.length ? product.images : [product.image])
  ).filter(Boolean);
  const mainImg = images[Math.min(activeImg, images.length - 1)] || product.image;
  const imgLoading = loadedSrc !== mainImg;
  const goToImg = (i) => setActiveImg(((i % images.length) + images.length) % images.length);
  const onTouchStart = (e) => { if (!e.touches || !e.touches[0]) return; touchStartX.current = e.touches[0].clientX; touchDeltaX.current = 0; };
  const onTouchMove = (e) => { if (touchStartX.current == null || !e.touches || !e.touches[0]) return; touchDeltaX.current = e.touches[0].clientX - touchStartX.current; };
  const onTouchEnd = () => {
    const dx = touchDeltaX.current;
    touchStartX.current = null;
    if (images.length > 1 && Math.abs(dx) > 40) {
      swipedRef.current = true;
      goToImg(activeImg + (dx < 0 ? 1 : -1));
      setTimeout(() => { swipedRef.current = false; }, 60);
    }
  };

  return (
    <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="product-detail-page">
      <Seo
        title={`${product.name}${product.subtitle ? ` - ${product.subtitle}` : ""}`}
        description={`${product.name} at Zebaa, Green Street London. ${cleanDescription || product.subtitle || ""}`.slice(0, 300)}
        path={`/product/${product.slug}`}
        image={product.image}
        jsonLd={[productSchema(product), breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: product.category.replace("-", " "), path: `/shop/${product.category}` },
          { name: product.name, path: `/product/${product.slug}` },
        ])]}
      />
      <nav className="flex items-center gap-2 text-xs text-gray-400 mb-8">
        <Link to="/" className="hover:text-pink">Home</Link>
        <ChevronRight className="w-3 h-3" />
        <Link to={`/shop/${product.category}`} className="hover:text-pink capitalize">{product.category.replace("-", " ")}</Link>
        <ChevronRight className="w-3 h-3" />
        <span className="text-charcoal">{product.name}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
        <div>
          <div
            className="relative bg-surface group cursor-zoom-in flex items-center justify-center aspect-[3/4] max-h-[74vh] overflow-hidden select-none touch-pan-y"
            onClick={() => { if (!swipedRef.current) setLightbox(true); }}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            data-testid="product-gallery-main"
          >
            <img
              key={mainImg}
              src={mainImg}
              alt={product.name}
              draggable={false}
              onLoad={() => setLoadedSrc(mainImg)}
              className={`w-full h-full object-contain transition-opacity duration-500 ease-out ${imgLoading ? "opacity-0" : "opacity-100"}`}
            />
            {imgLoading && (
              <div
                className="absolute inset-0 pointer-events-none overflow-hidden bg-surface animate-pulse"
                data-testid="gallery-loading"
                aria-hidden="true"
              >
                <div className="pk-shimmer absolute inset-0" />
              </div>
            )}
            {product.on_sale && (
              <span className="absolute top-4 left-4 bg-pink text-white text-[11px] uppercase tracking-widest px-3 py-1.5">Sale</span>
            )}
            {images.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); goToImg(activeImg - 1); }}
                  data-testid="gallery-prev"
                  aria-label="Previous image"
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/85 hover:bg-white shadow-md flex items-center justify-center text-charcoal opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); goToImg(activeImg + 1); }}
                  data-testid="gallery-next"
                  aria-label="Next image"
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/85 hover:bg-white shadow-md flex items-center justify-center text-charcoal opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 sm:hidden" data-testid="gallery-dots">
                  {images.map((_, i) => (
                    <span key={i} className={`w-1.5 h-1.5 rounded-full transition-colors ${i === activeImg ? "bg-pink" : "bg-white/70 ring-1 ring-black/10"}`} />
                  ))}
                </div>
              </>
            )}
            {images.length > 1 && (
              <span className="absolute bottom-4 right-4 bg-black/55 text-white text-[11px] px-2.5 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hidden sm:block">
                {activeImg + 1} / {images.length} · Click to zoom
              </span>
            )}
          </div>
          {images.length > 1 && (
            <div className="flex gap-2.5 mt-3 overflow-x-auto pb-1" data-testid="product-gallery-thumbs">
              {images.map((src, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setActiveImg(i)}
                  data-testid={`product-thumb-${i}`}
                  className={`shrink-0 w-16 h-20 sm:w-20 sm:h-24 overflow-hidden border rounded-sm transition-all ${
                    i === activeImg ? "border-pink ring-1 ring-pink" : "border-gray-200 hover:border-gray-400"
                  }`}
                >
                  <img src={src} alt={`${product.name} view ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="lg:py-6">
          <p className="font-clean text-xs uppercase tracking-[0.16em] font-semibold text-pink">{product.category.replace("-", " ")}</p>
          <h1 className="font-clean font-extrabold text-3xl lg:text-[2.75rem] leading-[1.05] mt-2.5 text-charcoal">{product.name}</h1>
          <p className="font-clean text-base text-gray-500 mt-1.5">{product.subtitle}</p>
          <ProductBadges product={product} className="mt-3" exclude={["trending", "baskets"]} />

          <div className="flex items-center gap-3 mt-5 font-clean">
            <span className="text-3xl font-extrabold text-charcoal">{format(product.price)}</span>
            {product.original_price && (
              <span className="text-lg text-gray-400 line-through">{format(product.original_price)}</span>
            )}
            {product.original_price && product.original_price > product.price && (
              <span
                className="inline-flex items-center gap-1 text-xs font-semibold text-pink bg-pink/10 px-2.5 py-1 rounded-full"
                data-testid="detail-discount-percent"
              >
                <Tag className="w-3.5 h-3.5" /> {Math.round((1 - product.price / product.original_price) * 100)}% OFF
              </span>
            )}
          </div>
          <p className="font-clean text-xs text-gray-400 mt-1.5" data-testid="price-tax-note">All prices include taxes.</p>
          <LiveActivity product={product} className="mt-3" />

          <div className="w-full h-px bg-gray-100 my-8" />

          <p className="font-clean text-[15px] sm:text-base text-gray-700 leading-relaxed">{cleanDescription}</p>

          {colourStock.length > 0 && (
            <div className="mt-8" data-testid="color-selector">
              <div className="flex items-baseline justify-between mb-3">
                <span className="text-xs uppercase tracking-[0.18em] text-charcoal">
                  Colour <span className="text-gray-400 normal-case tracking-normal ml-1">· {selectedColor}</span>
                </span>
                {selectedStock !== null && (
                  <span className={`text-xs ${soldOut ? "text-red-500" : selectedStock <= 3 ? "text-pink" : "text-gray-400"}`} data-testid="colour-stock">
                    {soldOut ? "Out of stock" : selectedStock <= 3 ? `Only ${selectedStock} left` : "In stock"}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-3">
                {colourStock.map((c) => {
                  const hex = colourHex(c.colour);
                  const out = c.stock !== null && c.stock <= 0;
                  const active = selectedColor === c.colour;
                  return (
                    <button key={c.colour} onClick={() => !out && setSelectedColor(c.colour)} title={c.colour}
                      data-testid={`colour-${c.colour}`} disabled={out}
                      className={`relative w-9 h-9 rounded-full transition-transform ${active ? "ring-2 ring-pink ring-offset-2" : "ring-1 ring-gray-200 ring-offset-1 hover:scale-110"} ${out ? "opacity-40 cursor-not-allowed" : ""} ${isLightColour(hex) ? "border border-gray-300" : ""}`}
                      style={{ backgroundColor: hex }}>
                      {active && <Check className={`w-4 h-4 absolute inset-0 m-auto ${isLightColour(hex) ? "text-charcoal" : "text-white"}`} />}
                      {out && <span className="absolute inset-0 flex items-center justify-center"><span className="w-full h-px bg-gray-400 rotate-45" /></span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {product.category !== "jewellery" && (
            <div className="mt-8">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs uppercase tracking-[0.18em] text-charcoal">
                  Size{availableSizes.length > 0 && selectedSize ? <span className="text-gray-400 normal-case tracking-normal ml-1">· {selectedSize}</span> : ""}
                </span>
                <Link to="/size-guide" data-testid="size-guide-link" className="flex items-center gap-1.5 text-xs text-pink hover:underline">
                  <Ruler className="w-3.5 h-3.5" /> Size Guide
                </Link>
              </div>
              {availableSizes.length > 0 ? (
                <div className="flex flex-wrap gap-2.5" data-testid="size-selector">
                  {availableSizes.map((s) => {
                    const out = sizeSoldOut(s);
                    return (
                      <button key={s} onClick={() => { if (!out) { setSelectedSize(s); setCustomization(null); setCustomConfirmed(false); } }} disabled={out}
                        data-testid={`size-${s}`}
                        className={`relative min-w-[48px] px-4 py-2.5 text-sm border rounded-sm transition-colors ${selectedSize === s ? "border-pink text-pink bg-pink/5" : "border-gray-200 text-gray-600 hover:border-charcoal"} ${out ? "opacity-40 cursor-not-allowed line-through" : ""}`}>
                        {s}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-400">Standard fit. Check the size guide for measurements.</p>
              )}
            </div>
          )}

          {canCustomise && (
            <div className="mt-8" data-testid="customise-fit-section">
              <button
                type="button"
                onClick={() => setFitOpen(true)}
                data-testid="customise-fit-button"
                className="w-full flex items-center justify-center gap-2 border border-gold/60 text-charcoal rounded-xl px-5 py-3.5 text-sm font-medium hover:bg-gold/5 hover:border-gold transition-colors"
              >
                <Scissors className="w-4 h-4 text-gold" />
                {customization ? "Edit Custom Fit" : "Customise Fit"}
              </button>
              {customization ? (
                <div className="mt-3 bg-surface rounded-xl p-4" data-testid="customise-fit-selected">
                  <CustomFitSummary customization={customization} />
                </div>
              ) : (
                <p className="text-xs text-gray-400 mt-2 text-center">Tailor this garment to your measurements — <span className="text-charcoal font-medium">+£10</span>. Made to order, allow 3–5 working days for dispatch.</p>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-stretch gap-3 mt-9">
            <div className="flex items-center border border-gray-200 rounded-sm order-1">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3.5 py-3.5 hover:text-pink" data-testid="qty-decrease">
                <Minus className="w-4 h-4" />
              </button>
              <span className="px-4 text-sm w-12 text-center" data-testid="qty-value">{qty}</span>
              <button
                onClick={() => setQty((q) => (maxQty !== null ? Math.min(maxQty, q + 1) : q + 1))}
                disabled={maxQty !== null && qty >= maxQty}
                className="px-3.5 py-3.5 hover:text-pink disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-current"
                data-testid="qty-increase"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={() => addToCart(product, qty, { colour: selectedColor, size: selectedSize, customization, custom_confirmed: customConfirmed })}
              disabled={soldOut || needsSize}
              data-testid="detail-add-to-bag"
              className="order-3 sm:order-2 basis-full sm:basis-0 sm:flex-1 bg-pink text-white text-sm font-medium uppercase tracking-[0.14em] whitespace-nowrap px-6 py-4 flex items-center justify-center gap-2 hover:bg-pink-hover transition-colors rounded-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ShoppingBag className="w-4 h-4 shrink-0" /> {soldOut ? "Out of Stock" : needsSize ? "Select a Size" : "Add to Bag"}
            </button>
            <button
              onClick={() => toggleWishlist(product)}
              data-testid="detail-wishlist"
              aria-label="Add to wishlist"
              className="order-2 sm:order-3 flex-1 sm:flex-none px-4 py-3.5 border border-gray-200 rounded-sm flex items-center justify-center hover:border-pink transition-colors"
            >
              <Heart className={`w-5 h-5 ${wished ? "fill-pink text-pink" : "text-gray-500"}`} />
            </button>
            <button
              onClick={handleShare}
              data-testid="detail-share"
              aria-label="Share this product"
              className="order-2 sm:order-4 flex-1 sm:flex-none px-4 py-3.5 border border-gray-200 rounded-sm flex items-center justify-center hover:border-pink hover:text-pink text-gray-500 transition-colors"
            >
              <Share2 className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-2.5 mt-8 pt-6 border-t border-gray-100 font-clean text-sm text-gray-600">
            {!soldOut && (
              <div className="flex items-center gap-2.5" data-testid="detail-in-stock">
                {selectedStock !== null && selectedStock <= 2 ? (
                  <>
                    <span className="inline-block w-2 h-2 rounded-full bg-amber-500 shrink-0 animate-pulse" />
                    <span className="text-amber-600 font-medium">Low in stock &ndash; Selling Fast</span>
                  </>
                ) : (
                  <>
                    <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                    <span>In Stock &ndash; Ships within 1&ndash;3 days</span>
                  </>
                )}
              </div>
            )}
            <div className="flex items-center gap-2.5"><Truck className="w-4 h-4 text-gold shrink-0" /> Free UK delivery on orders over £40</div>
            <div className="flex items-center gap-2.5"><ShieldCheck className="w-4 h-4 text-gold shrink-0" /> Secure checkout</div>
          </div>

          <ProductInfoSections product={product} />
        </div>
      </div>

      {related.length > 0 && (
        <div className="mt-24">
          <h2 className="font-serif text-3xl text-center text-charcoal mb-10">You May Also Like</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
            {related.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </div>
      )}
      {canCustomise && (
        <CustomiseFitModal
          product={product}
          size={selectedSize}
          open={fitOpen}
          onClose={() => setFitOpen(false)}
          initial={customization}
          onApply={(cust, confirmed) => { setCustomization(cust); setCustomConfirmed(!!confirmed); }}
        />
      )}

      {lightbox && (
        <div
          className="fixed inset-0 z-[70] bg-black/90 flex flex-col overflow-hidden animate-fade-in"
          onClick={() => setLightbox(false)}
          data-testid="product-lightbox"
        >
          <button
            type="button"
            onClick={() => setLightbox(false)}
            data-testid="lightbox-close"
            className="absolute top-4 right-4 z-10 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
          <div className="flex-1 flex items-center justify-center px-4 sm:px-16 min-h-0" onClick={(e) => e.stopPropagation()}>
            {images.length > 1 && (
              <button
                type="button"
                onClick={() => setActiveImg((i) => (i - 1 + images.length) % images.length)}
                data-testid="lightbox-prev"
                className="absolute left-2 sm:left-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
                aria-label="Previous"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
            )}
            <img src={mainImg} alt={product.name} className="max-h-full max-w-full object-contain" />
            {images.length > 1 && (
              <button
                type="button"
                onClick={() => setActiveImg((i) => (i + 1) % images.length)}
                data-testid="lightbox-next"
                className="absolute right-2 sm:right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
                aria-label="Next"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            )}
          </div>
          {images.length > 1 && (
            <div className="shrink-0 flex gap-2.5 justify-center overflow-x-auto px-4 py-5" onClick={(e) => e.stopPropagation()}>
              {images.map((src, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setActiveImg(i)}
                  data-testid={`lightbox-thumb-${i}`}
                  className={`shrink-0 w-14 h-20 sm:w-16 sm:h-20 overflow-hidden rounded-sm border-2 transition-all ${
                    i === activeImg ? "border-white" : "border-transparent opacity-60 hover:opacity-100"
                  }`}
                >
                  <img src={src} alt={`View ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
